import { useEffect, useRef, useState } from 'react';

/** Animates a number counting up (or down) to `value` on every change,
 * instead of the text just snapping to the new figure. Starts from 0 the
 * first time it mounts (a little "reveal" on page load), then eases from
 * whatever it last displayed on every value after that — e.g. a balance
 * ticking up when a win comes in over the socket, rather than jumping.
 * Respects prefers-reduced-motion by skipping straight to the final value. */
export default function CountUp({ value, duration = 900, formatter, as: Tag = 'span', className, style }) {
  const numeric = Number(value) || 0;
  const [display, setDisplay] = useState(0);
  const [pop, setPop] = useState(false);
  const prevRef = useRef(0);
  const rafRef = useRef(0);
  const popTimerRef = useRef(0);

  useEffect(() => {
    const from = prevRef.current;
    const to = numeric;
    if (from === to) return undefined;

    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setDisplay(to);
      prevRef.current = to;
      return undefined;
    }

    // Small bounce once the number lands, so the count-up has a punctuation
    // mark instead of just quietly stopping.
    function settle() {
      setDisplay(to);
      prevRef.current = to;
      setPop(true);
      clearTimeout(popTimerRef.current);
      popTimerRef.current = setTimeout(() => setPop(false), 320);
    }

    const start = performance.now();
    cancelAnimationFrame(rafRef.current);
    function tick(now) {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic — fast start, gentle settle
      setDisplay(from + (to - from) * eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        settle();
      }
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      clearTimeout(popTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numeric, duration]);

  const shown = formatter ? formatter(display) : Math.round(display).toLocaleString();
  return (
    <Tag className={`${className || ''}${pop ? ' count-pop' : ''}`} style={style}>
      {shown}
    </Tag>
  );
}
